import React, { useMemo, useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import { DeviceSize } from "constants/deviceSize";
import { loadDb, Workspace, setTrackReaction, Track, fetchPlaylists } from "../data/schema";
import EditIcon from "../../../assets/edit-ws.svg";
import BackIcon from "../../../assets/back-ws.svg";
import LikeIcon from "../../../assets/like.svg";
import DislikeIcon from "../../../assets/dislike.svg";
import ExportIcon from "../../../assets/export.svg";
import MoreIcon from "../../../assets/more.svg";
import { EditWorkspaceModal } from "./Modals/EditWorkspaceModal";
import { TrackModal } from "./TrackModal";
import { TrackMenu } from "./TrackMenu";
import { AddToPlaylistModal } from "./Modals/AddToPlaylistModal";
import { MoveToWorkspaceModal } from "./Modals/MoveToWorkspaceModal";
import { ReportBugModal } from "./Modals/ReportBugModal";
import { ShareModal } from "./Modals/ShareModal";
import { FiltersModal, Filters } from "./FiltersModal";
import { showToast } from "components/ui-kit/Toast";
import PlayIcon from "../../../assets/play-player.svg";
import { CreatePersonaModal } from "components/AICompose/RightPanel/Modals/CreatePersonaModal";
import {
  AUDIO_LOAD_END_EVENT,
  AUDIO_LOAD_START_EVENT,
  WORKSPACE_TRACK_MENU_REQUEST_EVENT,
} from "components/AICompose/events";


type Props = {
  workspaces: Workspace[];
  workspaceSearch: string;
  onChangeSearch: (value: string) => void;
  onSelect: (workspaceId: string) => void;
  onMoveToTrash: (workspaceId: string) => void | Promise<void>;
  onBack: () => void;
  selectedWorkspace?: Workspace | null;
  onPlayTrack: (track: Track | null, sourceTracks?: Track[]) => void;
  hasPlayer?: boolean;
  playingTrack?: Track | null;
  isPlaying?: boolean;
  onTogglePlay?: () => void;
  onWorkspaceRenamed?: (workspaceId: string) => void | Promise<void>;
  isGeneratingTrack?: boolean;
  focusRequestToken?: number;
  trackMenuRequestToken?: number;
  onPersonaCreated?: () => void;
};

// Same as WorkspacesList, but with a back button at the top-left
export const WorkspacesView: React.FC<Props> = ({
  workspaces,
  workspaceSearch,
  onChangeSearch,
  onSelect,
  onMoveToTrash,
  onBack,
  selectedWorkspace,
  onPlayTrack,
  hasPlayer = false,
  playingTrack,
  isPlaying = false,
  onTogglePlay,
  onWorkspaceRenamed,
  isGeneratingTrack,
  focusRequestToken,
  trackMenuRequestToken,
  onPersonaCreated,
}) => {
  const [query, setQuery] = useState("");
  const [openEdit, setOpenEdit] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);
  const [selectedTracks, setSelectedTracks] = useState<Set<string>>(new Set());
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [submenuOpen, setSubmenuOpen] = useState<'share' | 'download' | 'report' | null>(null);
  const [addToPlaylistTrack, setAddToPlaylistTrack] = useState<Track | null>(null);
  const [moveToWorkspaceTrack, setMoveToWorkspaceTrack] = useState<Track | null>(null);
  const [reportBugTrack, setReportBugTrack] = useState<Track | null>(null);
  const [shareTrack, setShareTrack] = useState<Track | null>(null);
  const [createPersonaTrack, setCreatePersonaTrack] = useState<Track | null>(null);
  const [audioLoadingTrackIds, setAudioLoadingTrackIds] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set<string>();
    const anyWindow = window as any;
    const globalSet: Set<string> | undefined = anyWindow.__audioLoadingTrackIds;
    return globalSet ? new Set(globalSet) : new Set<string>();
  });
  // remember last opened workspace for Save to... control in Custom
  React.useEffect(() => {
    if (selectedWorkspace?.id) {
      try {
        localStorage.setItem('aiCompose.lastWorkspaceId', selectedWorkspace.id);
        localStorage.setItem('aiCompose.saveWorkspaceId', selectedWorkspace.id);
      } catch {}
      window.dispatchEvent(new CustomEvent('workspaceSelected', { detail: { workspaceId: selectedWorkspace.id } }));
    }
  }, [selectedWorkspace?.id]);

  // Listen for track creation events
  React.useEffect(() => {
    const handleTrackCreated = () => {
      setRefreshKey(k => k + 1);
    };
    window.addEventListener('trackCreated', handleTrackCreated);
    return () => window.removeEventListener('trackCreated', handleTrackCreated);
  }, []);
  
  // Listen for track reaction changes
  React.useEffect(() => {
    const handleReactionChanged = () => {
      setRefreshKey(k => k + 1);
    };
    window.addEventListener('trackReactionChanged', handleReactionChanged);
    return () => window.removeEventListener('trackReactionChanged', handleReactionChanged);
  }, []);

  // Live-update track metadata (title, description, styles, artwork, audioUrl, etc)
  React.useEffect(() => {
    const handleTrackUpdated = () => {
      setRefreshKey(k => k + 1);
    };
    window.addEventListener('trackUpdated', handleTrackUpdated);
    return () => window.removeEventListener('trackUpdated', handleTrackUpdated);
  }, []);
  
  // Close menu when clicking outside
  React.useEffect(() => {
    if (!menuOpenId) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-track-menu]')) {
        setMenuOpenId(null);
        setSubmenuOpen(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpenId]);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    liked: false,
    fullSongs: false,
    hasPersonas: false,
    hideDisliked: false,
  });
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' >('newest');
  const tracks = useMemo(() => {
    const db = loadDb();
    const ws = selectedWorkspace;
    if (!ws) return [] as any[];
    const currentWs = db.workspaces[ws.id];
    if (!currentWs) return [] as any[];

    const list = currentWs.trackIds
      .map(id => db.tracks[id])
      .filter((track): track is Track => Boolean(track))
      .filter(track => !track.trashed);
    
    const withIndex = list.map((track, idx) => ({ track, idx }));
    withIndex.sort((a, b) => {
      const aLoading = Boolean(a.track.params?.isLoading);
      const bLoading = Boolean(b.track.params?.isLoading);
      if (aLoading === bLoading) return a.idx - b.idx;
      return aLoading ? -1 : 1;
    });

    let ordered = withIndex.map(item => item.track);

    const q = (query || workspaceSearch).toLowerCase().trim();
    if (q) {
      ordered = ordered.filter((t: any) =>
        t.title?.toLowerCase().includes(q) ||
        t.params?.songDescription?.toLowerCase().includes(q) ||
        t.params?.stylesDescription?.toLowerCase().includes(q)
      );
    }
    
   
    if (filters.liked) {
      ordered = ordered.filter((t: any) => t.reaction === 'like');
    }
    if (filters.hideDisliked) {
      ordered = ordered.filter((t: any) => t.reaction !== 'dislike');
    }
    if (filters.fullSongs) {
      ordered = ordered.filter((t: any) => t.fullSongs !== false);
    }
    if (filters.hasPersonas) {
      ordered = ordered.filter((t: any) => t.hasPersonas === true);
    }
    
    if (sortBy === 'oldest') {
      ordered = [...ordered].sort((a: any, b: any) => a.createdAt - b.createdAt);
    } else if (sortBy === 'newest') {
      ordered = [...ordered].sort((a: any, b: any) => b.createdAt - a.createdAt);
    } 

    return ordered;
  }, [selectedWorkspace, refreshKey, query, workspaceSearch, filters, sortBy]);

  const isTrackLoading = useCallback(
    (track: Track) => Boolean(track.params?.isLoading) || audioLoadingTrackIds.has(track.id),
    [audioLoadingTrackIds]
  );

  React.useEffect(() => {
    const handleAudioLoadStart = (event: Event) => {
      const detail = (event as CustomEvent<{ trackId: string }>).detail;
      if (!detail?.trackId) return;
      setAudioLoadingTrackIds((prev) => {
        if (prev.has(detail.trackId)) return prev;
        const next = new Set(prev);
        next.add(detail.trackId);
        return next;
      });
    };
    const handleAudioLoadEnd = (event: Event) => {
      const detail = (event as CustomEvent<{ trackId: string }>).detail;
      if (!detail?.trackId) return;
      setAudioLoadingTrackIds((prev) => {
        if (!prev.has(detail.trackId)) return prev;
        const next = new Set(prev);
        next.delete(detail.trackId);
        return next;
      });
    };
    window.addEventListener(AUDIO_LOAD_START_EVENT, handleAudioLoadStart as EventListener);
    window.addEventListener(AUDIO_LOAD_END_EVENT, handleAudioLoadEnd as EventListener);
    return () => {
      window.removeEventListener(AUDIO_LOAD_START_EVENT, handleAudioLoadStart as EventListener);
      window.removeEventListener(AUDIO_LOAD_END_EVENT, handleAudioLoadEnd as EventListener);
    };
  }, []);

  const openTopTrackMenu = useCallback(() => {
    if (!tracks.length) return;
    const topTrack = tracks[0];
    if (topTrack) {
      setMenuOpenId(topTrack.id);
      setSelectedTrack(null);
    }
  }, [tracks]);
 
  useEffect(() => {
    if (!focusRequestToken) return;
    openTopTrackMenu();
  }, [focusRequestToken, openTopTrackMenu]);

  useEffect(() => {
    if (!trackMenuRequestToken) return;
    openTopTrackMenu();
  }, [trackMenuRequestToken, openTopTrackMenu]);

  return (
    <ViewContainer $hasModal={!!selectedTrack}>
      <ContentWrapper $hasModal={!!selectedTrack} $hasPlayer={hasPlayer}>
        <FixedHeader $hasModal={!!selectedTrack}>
          <HeaderRow>
            <BackButton onClick={onBack}>
              <BackIcon />
            </BackButton>
            <TitleRow>
              <WorkspacesTitle>{selectedWorkspace?.name || 'Workspace'}</WorkspacesTitle>
              <EditButton onClick={() => setOpenEdit(true)}>
                <EditIcon />
              </EditButton>
            </TitleRow>
          </HeaderRow>
          <ControlsRow>
        <SearchInput
          name="workspaceSearch"
          placeholder="Search"
          value={query || workspaceSearch}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
        />
          <DropdownWrapper>
            <PillWithCaret
              $active={isFiltersOpen}
              onClick={() => {
                setIsFiltersOpen(v => !v);
                setIsSortOpen(false);
              }}
            >
              {(() => {
                const activeCount =
                  (filters.liked ? 1 : 0) +
                  (filters.fullSongs ? 1 : 0) +
                  (filters.hasPersonas ? 1 : 0) +
                  (filters.hideDisliked ? 1 : 0);
                return activeCount > 0 ? `Filters (${activeCount})` : "Filters";
              })()}
            </PillWithCaret>
            {isFiltersOpen && (
              <FiltersModal
                filters={filters}
                onChange={(next) => {
                  setFilters(next);
                }}
                onClose={() => setIsFiltersOpen(false)}
              />
            )}
          </DropdownWrapper>
        <DropdownWrapper>
          <PillWithCaret $active={isSortOpen} onClick={() => { setIsSortOpen(v => !v); setIsFiltersOpen(false); }}>
            {sortBy === 'newest' ? 'Newest' : sortBy === 'oldest' ? 'Oldest' : 'OldeSt'}
          </PillWithCaret>
          {isSortOpen && (
            <DropdownMenu onClick={(e) => e.stopPropagation()}>
              <DropdownItem>
                <label>
                  <span>Newest</span>
                  <input type="checkbox" checked={sortBy === 'newest'} onChange={() => { setSortBy('newest'); setIsSortOpen(false); }} />
                </label>
              </DropdownItem>
              <DropdownItem>
                <label>
                  <span>Oldest</span>
                  <input type="checkbox" checked={sortBy === 'oldest'} onChange={() => { setSortBy('oldest'); setIsSortOpen(false); }} />
                </label>
              </DropdownItem>
              
            </DropdownMenu>
          )}
        </DropdownWrapper>
        {/* <Pill>Liked</Pill>
        <Pill>Public</Pill>
        <Pill>Uploads</Pill> */}
          </ControlsRow>
        </FixedHeader>
        <ScrollableContent>
      {tracks.length === 0 ? (
        <EmptyState>
          <EmptyText>
            {filters.liked ||
            filters.fullSongs ||
            filters.hasPersonas ||
            filters.hideDisliked
              ? "No songs found matching your filters"
              : "There are no songs in this workspace yet."}
          </EmptyText>
        </EmptyState>
      ) : (
      <TrackList $hasPlayer={hasPlayer}>
          {tracks.map((track: any) => {
            const params = (track.params || {}) as Record<string, any>;
            const isCustomMode =
              params.customMode === true ||
              params.mode === 'custom';
            const mode = isCustomMode ? 'custom' : 'easy';

            let subtitleText = '';
            if (mode === 'easy') {
              const prompt: string = (
                params.songDescription ??
                params.prompt ??
                track.description ??
                ''
              ).toString();
              subtitleText = prompt;
            } else {
              const styles: string = (
                params.stylesDescription ??
                params.style ??
                ''
              ).toString();
              subtitleText = styles || (track.description || '').toString();
            }

            const cleanedSubtitle = subtitleText
              ? subtitleText.replace(/\s+/g, ' ').trim()
              : '';
            const isMobile =
              typeof window !== 'undefined'
                ? window.innerWidth <= 768
                : false;
            const baseMax = selectedTrack ? 25 : 80;
            const maxLength = isMobile ? Math.max(10, Math.floor(baseMax / 2)) : baseMax;
            const subtitleShort = cleanedSubtitle
              ? cleanedSubtitle.length > maxLength
                ? `${cleanedSubtitle.slice(0, maxLength)}...`
                : cleanedSubtitle
              : '';
            const fullTitle = (track.title || 'Song').toString();
            const titleMax = isMobile ? 17 : 30;
            const displayTitle =
              fullTitle.length > titleMax ? `${fullTitle.slice(0, 10)}...` : fullTitle;
            const reaction = track.reaction || null;
            const playableUrl = track.params?.localAudioUrl || null;
            const artworkUrl =
              track.params?.localImageUrl ||
              track.params?.imageUrl ||
              null;
            const durationLabel =
              typeof track.durationSec === 'number' && track.durationSec > 0
                ? `${Math.floor(Math.round(track.durationSec) / 60)}:${(Math.round(track.durationSec) % 60)
                    .toString()
                    .padStart(2, '0')}`
                : null;
            return (
            <TrackItem key={track.id}>
              <CheckboxWrapper>
                <Checkbox 
                  $visible={selectedTracks.has(track.id)}
                  type="checkbox"
                  checked={selectedTracks.has(track.id)}
                  onChange={(e) => {
                    const newSelected = new Set(selectedTracks);
                    if (e.target.checked) {
                      newSelected.add(track.id);
                    } else {
                      newSelected.delete(track.id);
                    }
                    setSelectedTracks(newSelected);
                  }}
                />
              </CheckboxWrapper>
              <ThumbnailWrapper
                $isLoading={isTrackLoading(track)}
                onClick={() => {
                  if (isTrackLoading(track)) return;
                  console.debug('[WorkspacesView] Play track requested', {
                    trackId: track.id,
                    title: track.title,
                    isLoading: isTrackLoading(track),
                    playableUrl,
                  });
                  if (!playableUrl) {
                    showToast('Audio is not available yet. Please wait a bit longer.');
                    console.warn('[WorkspacesView] Missing playable URL for track', {
                      trackId: track.id,
                      params: track.params,
                    });
                    return;
                  }
                const isThisTrack = playingTrack?.id === track.id;
                  if (isThisTrack) {
                    onTogglePlay?.();
                } else {
                    console.debug('[WorkspacesView] Dispatching track to player', {
                      trackId: track.id,
                      title: track.title,
                    });
                    onPlayTrack(track);
                }
              }}>
                <Thumbnail $imageUrl={artworkUrl} />
                <PlayOverlay $isLoading={isTrackLoading(track)}>
                  {isTrackLoading(track) ? (
                    <LoaderSpinner />
                  ) : playingTrack?.id === track.id && isPlaying ? (
                    <svg viewBox="0 0 24 24" style={{ width: '20px', height: '20px', fill: '#6777FF'}}>
                    <rect x="6" y="4" width="4" height="16" fill='#6777FF' />
                    <rect x="14" y="4" width="4" height="16" fill='#6777FF' />
                  </svg>
                  ) : (
                    <PlayIcon />
                  )}
                </PlayOverlay>
                {durationLabel && !isTrackLoading(track) && (
                  <DurationBadge>{durationLabel}</DurationBadge>
                )}
              </ThumbnailWrapper>
              <TrackInfo onClick={() => {
                if (isTrackLoading(track)) return;
                setSelectedTrack(track);
              }}>
                <TrackTitle>
                  {displayTitle}
                </TrackTitle>
                <TrackSubtitle>{subtitleShort}</TrackSubtitle>
                <TrackActions onClick={(e) => e.stopPropagation()}>
                  <ReactionButton
                    title="Like"
                    $active={reaction === 'like'}
                    onClick={async (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (isTrackLoading(track)) return;
                      const next = reaction === 'like' ? null : 'like'; 
                      // setTrackReaction does optimistic update and dispatches trackReactionChanged event
                      // which will trigger handleReactionChanged to update UI immediately
                      try {
                        await setTrackReaction(track.id, next as any);
                      } catch (error) {
                        console.error('Failed to set track reaction:', error);
                        showToast('Failed to update reaction', undefined, 'error');
                        // Error handling and rollback is done inside setTrackReaction
                      }
                    }}
                    disabled={isTrackLoading(track)}
                  >
                    <LikeIcon />
                  </ReactionButton>
                  <ReactionButton
                    title="Dislike"
                    $active={reaction === 'dislike'}
                    onClick={async (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (isTrackLoading(track)) return;
                      const next = reaction === 'dislike' ? null : 'dislike'; 
                      // setTrackReaction does optimistic update and dispatches trackReactionChanged event
                      // which will trigger handleReactionChanged to update UI immediately
                      try {
                        await setTrackReaction(track.id, next as any);
                      } catch (error) {
                        console.error('Failed to set track reaction:', error);
                        showToast('Failed to update reaction', undefined, 'error');
                        // Error handling and rollback is done inside setTrackReaction
                      }
                    }}
                    disabled={isTrackLoading(track)}
                  >
                    <DislikeIcon />
                  </ReactionButton>
                  <ActionButton 
                    title="Export"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (isTrackLoading(track)) return;
                      setShareTrack(track);
                    }}
                    disabled={isTrackLoading(track)}
                  >
                    <ExportIcon />
                  </ActionButton>
                </TrackActions>
              </TrackInfo>
              <MoreWrapper onClick={(e) => e.stopPropagation()} data-track-menu>
                {!isTrackLoading(track) && (
                <MoreButton onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === track.id ? null : track.id); setSubmenuOpen(null); }}>⋯</MoreButton>
                )}
                {!isTrackLoading(track) && menuOpenId === track.id && (
                  <TrackMenu
                    trackId={track.id}
                    track={track}
                    onClose={() => { setMenuOpenId(null); setSubmenuOpen(null); }}
                    onAddToPlaylist={() => { 
                      // If multiple tracks selected, use the first one as representative
                      // The modal will handle all selected tracks
                      setAddToPlaylistTrack(track); 
                      setMenuOpenId(null); 
                      setSubmenuOpen(null); 
                    }}
                    onMoveToWorkspace={() => { 
                      // If multiple tracks selected, use the first one as representative
                      // The modal will handle all selected tracks
                      setMoveToWorkspaceTrack(track); 
                      setMenuOpenId(null); 
                      setSubmenuOpen(null); 
                    }}
                    onCreatePersona={() => {
                      setCreatePersonaTrack(track);
                      setMenuOpenId(null); 
                      setSubmenuOpen(null); 
                    }}
                    onAddToQueue={() => {}}
                    onMoveToTrash={() => { setRefreshKey(k => k + 1); setSelectedTracks(new Set()); }}
                    onReportBug={() => { 
                      setReportBugTrack(track); 
                    }}
                    onReportInappropriate={() => { 
                      showToast("Marked, thanks!", undefined, 'success');
                    }}
                    onShareTo={() => { 
                      setShareTrack(track); 
                    }}
                    selectedTracks={selectedTracks}
                    submenuOpen={submenuOpen}
                    onSubmenuOpen={setSubmenuOpen}
                  />
                )}
              </MoreWrapper>
            </TrackItem>
          );})}
        </TrackList>
      )}
        </ScrollableContent>
      </ContentWrapper>
      {selectedTrack && (
        <TrackModal
          track={selectedTrack}
          onClose={() => setSelectedTrack(null)}
          hasPlayer={hasPlayer}
          onPlayTrack={onPlayTrack}
          playingTrack={playingTrack}
          isPlaying={isPlaying}
          onTogglePlay={onTogglePlay}
        />
      )}
      <EditWorkspaceModal
        open={openEdit}
        workspace={selectedWorkspace || null}
        onClose={() => setOpenEdit(false)}
        onRenamed={async (workspaceId) => {
          setOpenEdit(false);
          await onWorkspaceRenamed?.(workspaceId);
        }}
      />
      <AddToPlaylistModal
        open={!!addToPlaylistTrack}
        track={addToPlaylistTrack}
        selectedTrackIds={selectedTracks.size > 0 ? selectedTracks : undefined}
        onClose={() => { setAddToPlaylistTrack(null); setSelectedTracks(new Set()); }}
        onAdded={async () => { 
          setRefreshKey(k => k + 1); 
          setSelectedTracks(new Set());
          // Refresh playlists to show updated track lists
          try {
            await fetchPlaylists();
          } catch (error) {
            console.error('Failed to refresh playlists:', error);
          }
        }}
      />
      <MoveToWorkspaceModal
        open={!!moveToWorkspaceTrack}
        track={moveToWorkspaceTrack}
        selectedTrackIds={selectedTracks.size > 0 ? selectedTracks : undefined}
        onClose={() => { setMoveToWorkspaceTrack(null); setSelectedTracks(new Set()); }}
        onMoved={() => { setRefreshKey(k => k + 1); setSelectedTracks(new Set()); }}
      />
      <ReportBugModal
        open={!!reportBugTrack}
        track={reportBugTrack}
        onClose={() => { setReportBugTrack(null); }}
      />
      <ShareModal
        open={!!shareTrack}
        track={shareTrack}
        onClose={() => { setShareTrack(null); }}
      />
      <CreatePersonaModal
        open={!!createPersonaTrack}
        track={createPersonaTrack}
        onClose={() => setCreatePersonaTrack(null)}
        onSuccess={() => {
          setRefreshKey(k => k + 1);
          setCreatePersonaTrack(null);
        }}
        origin="ai-compose"
        onPersonaCreated={onPersonaCreated}
      />
    </ViewContainer>
  );
};

const ViewContainer = styled.div<{ $hasModal: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  height: auto;
  overflow: hidden;
  max-height: 81vh;
  box-sizing: border-box;
  width: ${p => p.$hasModal ? 'calc(60% - 10px)' : '100%'};

  @media (max-width: ${DeviceSize.TabletMedium}) {
    width: 100%;
    max-height: 80vh;
  }
`;


const ContentWrapper = styled.div<{ $hasModal: boolean; $hasPlayer?: boolean }>`
  flex: 1;
  min-height: 0;
  width: 100%;
  max-height: 100%;
  transition: width 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: ${p => p.$hasPlayer ? '90px' : '0'};
  
`;

const FixedHeader = styled.div<{ $hasModal: boolean }>`
  position: sticky;
  top: 0;
  z-index: 10;
  background: ${props => props.theme.palette.background.primary || '#fff'};
  padding-bottom: 20px;
  padding-right:20px;
  width: 100%;
  transition: width 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 20px;
   @media (max-width: ${DeviceSize.TabletMedium}) {
  padding-right:0px;
}
`;

const ScrollableContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 20px;
  padding-right:20px;
  &::-webkit-scrollbar {
    display: none;
  }
  @media (max-width: ${DeviceSize.TabletMedium}) {
  padding-right:0px;
}
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  svg { width: 21px; height: 15px; }
`;

const WorkspacesTitle = styled.div`
  font-weight: 600;
  font-size: 18px;
  color: #333;
  flex: 0 1 auto;
  margin: 0 5px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  max-height: 2.8em;
`;

const EditButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  svg { width: 14px; height: 14px; }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1; 
  margin-left: 10px;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap; /* чтобы не ломать лейаут при узких ширинах */
  width: 100%;
  min-width: 0;
`;

const SearchInput = styled.input`
  flex: 1 1 40%; 
  max-width: 80%;
  min-width: 20%;
  height: 4.2vh;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  padding: 0 1vw;
  outline: none;
  box-sizing: border-box;
`;

const Pill = styled.button<{ $basis?: number }>`
  padding: 1vh 1vw;
  border-radius: 20px;
  border: 1px solid #e9ecef;
  background: #fff;
  color: #333;
  cursor: pointer;
  white-space: nowrap;
  flex: 0 0 auto;
  min-width: 0;
`;

const DropdownWrapper = styled.div`
  position: relative;
  display: flex;
`;

const PillWithCaret = styled(Pill)<{ $active?: boolean }>`
  padding-right: calc(1vw + 16px);
  position: relative;
  border-color: ${p => p.$active ? '#000' : '#e9ecef'};
  &::after {
    content: '';
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid #333;
    opacity: 0.7;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(0,0,0,0.12);
  padding: 8px 0;
  min-width: 240px;
  z-index: 20;
`;

const DropdownItem = styled.div`
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  label { cursor: pointer; user-select: none; display: flex; align-items: center; justify-content: space-between; width: 100%; }
  input { margin-left: 12px; }
  &:hover { background: #f8f9ff; }
`;

const DropdownItemButton = styled.button`
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  &:hover { background: #f8f9ff; }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 20px;
  height: 100%;
  min-height: 500px;
`;

const EmptyText = styled.div`
  color: #6c757d;
  font-size: 16px;
  text-align: center;
`;

const TrackList = styled.div<{ $hasPlayer?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
 @media (max-width: ${DeviceSize.TabletMedium}) {
    padding-bottom: ${p => p.$hasPlayer ? '9vh' : '0'};
}
`;

const CheckboxWrapper = styled.div`
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Checkbox = styled.input<{ $visible?: boolean }>`
  width: 18px;
  height: 18px;
  cursor: pointer;
  opacity: ${p => p.$visible ? 1 : 0};
  transition: opacity 0.15s ease;
`;

const Thumbnail = styled.div<{ $imageUrl?: string }>`
  width: 7.5vh;
  height: 9vh;
  border-radius: 6px;
  background: ${({ $imageUrl }) =>
    $imageUrl
      ? `url(${$imageUrl}) center center / cover no-repeat`
      : 'linear-gradient(135deg, #e8eaff, #f4f4f4)'};
  transition: opacity 0.15s ease;
`;

const DurationBadge = styled.div`
  position: absolute;
  right: 4px;
  bottom: 4px;
  padding: 2px 6px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
`;

const PlayOverlay = styled.div<{ $isLoading?: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: ${p => p.$isLoading ? 1 : 0};
  transition: opacity 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ThumbnailWrapper = styled.div<{ $isLoading?: boolean }>`
  position: relative;
  cursor: pointer;
  flex-shrink: 0;
  &:hover {
    ${Thumbnail} {
      opacity: ${p => p.$isLoading ? 1 : 0.7};
    }
    ${PlayOverlay} {
      opacity: 1;
    }
  }
`;

const TrackItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  background: #fff;
  margin: 3px 10px;
  transition: background 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    background: #fff;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    ${Checkbox} {
      opacity: 1;
    }
  }
  ${Checkbox}[checked] {
    opacity: 1 !important;
  }
`;

const TrackInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  cursor: pointer;
`;

const TrackTitle = styled.div`
  font-weight: 600;
  color: #333;
`;

const TrackSubtitle = styled.div`
  color: #6c757d;
  font-size: 12px;
`;

const TrackActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ReactionButton = styled.button<{ $active?: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${p => p.$active ? '#6777FF' : '#9aa0a6'};
  opacity: 0.7;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  &:hover { color: #6777FF; }
  svg { width: 20px; height: 20px; }
  svg * { fill: currentColor; }
  ${TrackItem}:hover & { opacity: 1; }
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6777FF;
  opacity: 0.7;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  &:hover { opacity: 1; }
  svg { width: 20px; height: 20px; }
  svg * { fill: currentColor; }
  ${TrackItem}:hover & { opacity: 1; }
`;

const MoreWrapper = styled.div`
  margin-left: auto;
  position: relative;
`;

const MoreButton = styled.button`
  width: 4vh;
  height: 4vh;
  border-radius: 4vh;
  border: 1px solid #e9ecef;
  background: #fff;
  cursor: pointer;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    width: 4vh;
    height: 4vh;
    border-radius: 50%;
  }
`;

const LoaderSpinner = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid rgba(103, 119, 255, 0.2);
  border-top-color: #6777FF;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

export default WorkspacesView;


